import React, { useState, useEffect, useRef } from 'react';
import { markReturnToProfile } from '../utils/returnToProfile';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import API_URL from '../config';
import LanguageSwitcher from './LanguageSwitcher';
import NotificationPreferences, { prefetchNotificationPreferences } from './NotificationPreferences';
import Avatar from './Avatar';
import { resizeImageFile } from '../utils/imageResize';
import ContactMessage from './ContactMessage';
import { getAuthToken, isImpersonating } from '../utils/authSession';
import { scrollPageToTop } from '../utils/nativeApp';
import './ProfileMenu.css';

// Eén profiel-icoontje rechts in de navbar i.p.v. losse "Welkom, X"-tekst +
// aparte uitlogknop, taalkeuze en uitlogknop, zodat de navbar op smalle
// telefoons niet meer uit zijn voegen barst. Bundelt: taal, wachtwoord
// wijzigen (mailt een reset-link naar de gebruiker zelf), meldingsvoorkeuren,
// contact opnemen en uitloggen.
// Capacitor injecht dit globaal object in de native app-shell (zelfde check
// als App.js/config.js) - target="_blank"-links doen in die native WebView
// niets (er is geen "nieuw tabblad" om naar te openen), dus daar moet
// intern via react-router genavigeerd worden i.p.v. een <a target="_blank">.
const isNativeMobileApp = () => {
  if (window.Capacitor && typeof window.Capacitor.isNativePlatform === 'function') {
    return window.Capacitor.isNativePlatform();
  }
  return false;
};

// SmartStays heeft aparte algemene voorwaarden per rol (zie Terms.js,
// TermsVerhuurder.js, TermsSchoonmaker.js) - een ADMIN is geen klant/
// schoonmaker en krijgt daarom de keuzepagina te zien i.p.v. één van de twee
// specifieke versies.
const termsPathForRole = (role) => {
  if (role === 'VERHUURDER') return '/terms/verhuurders';
  if (role === 'SCHOONMAKER') return '/terms/schoonmakers';
  return '/terms';
};

function ProfileMenu({ user, onLogout, screen = false, onOpenMyCode, onOpenProperties }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const termsPath = termsPathForRole(user?.role);
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirmingPassword, setConfirmingPassword] = useState(false);
  const [status, setStatus] = useState(null);
  const [showNotifPrefs, setShowNotifPrefs] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [menuTop, setMenuTop] = useState(64);
  const [showContact, setShowContact] = useState(false);
  const [contactPrefill, setContactPrefill] = useState('');
  // Bevestigingspopup i.p.v. meteen te versturen bij de eerste klik op
  // "Account verwijderen" - zie de modal-JSX verderop.
  const [confirmingDeletion, setConfirmingDeletion] = useState(false);
  const [deletionSending, setDeletionSending] = useState(false);
  const containerRef = useRef(null);
  // Eigen profielfoto: opgehaald via /api/me (de "user" prop komt uit
  // localStorage en kent dit veld niet), lokaal bijgewerkt na upload/verwijderen.
  const [photoUrl, setPhotoUrl] = useState(user?.photoUrl || null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const photoInputRef = useRef(null);

  // Als profielscherm (tabblad in de app) staat het vast: eerst terug naar boven,
  // want scrollen is daar uitgeschakeld (zie appTheme.css).
  useEffect(() => { prefetchNotificationPreferences(); }, []);

  useEffect(() => {
    if (screen) scrollPageToTop();
  }, [screen]);

  useEffect(() => {
    let cancelled = false;
    const loadPhoto = async () => {
      try {
        const token = getAuthToken();
        if (!token) return;
        const res = await axios.get(`${API_URL}/api/me`, { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled) setPhotoUrl(res.data.photoUrl || null);
      } catch (e) { /* geen foto tonen is prima */ }
    };
    loadPhoto();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const showStatus = (text, type) => {
    setStatus({ text, type });
    setTimeout(() => setStatus(null), 4000);
  };

  const handleChangePassword = async () => {
    setConfirmingPassword(false);
    try {
      setSending(true);
      const token = getAuthToken();
      await axios.post(`${API_URL}/api/request-password-reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showStatus(t('profileMenu.resetSent'), 'success');
    } catch (error) {
      showStatus(error.response?.data?.error || t('profileMenu.resetError'), 'error');
    } finally {
      setSending(false);
    }
  };

  const handlePhotoSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      setPhotoBusy(true);
      const blob = await resizeImageFile(file, 512);
      const formData = new FormData();
      formData.append('file', blob, 'profile.jpg');
      const token = getAuthToken();
      const res = await axios.post(`${API_URL}/api/me/photo`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPhotoUrl(res.data.photoUrl);
    } catch (error) {
      showStatus(error.response?.data?.error || t('profileMenu.photoError'), 'error');
    } finally {
      setPhotoBusy(false);
    }
  };

  const handleRemovePhoto = async () => {
    try {
      setPhotoBusy(true);
      const token = getAuthToken();
      await axios.delete(`${API_URL}/api/me/photo`, { headers: { Authorization: `Bearer ${token}` } });
      setPhotoUrl(null);
    } catch (error) {
      showStatus(error.response?.data?.error || t('profileMenu.photoError'), 'error');
    } finally {
      setPhotoBusy(false);
    }
  };

  // Native app: intern navigeren; website: nieuw tabblad.
  const openLegal = (path) => {
    setOpen(false);
    if (isNativeMobileApp()) { markReturnToProfile(); navigate(path); }
    else window.open(`${window.location.origin}${path}`, '_blank', 'noopener');
  };

  const handleContact = () => {
    setContactPrefill('');
    setOpen(false);
    setShowContact(true);
  };

  // Geen meteen-uitvoerende "verwijder nu"-actie (dat zou, gezien
  // boekhoudkundige/factuurhistoriek, data kunnen wegnemen die we wettelijk
  // nog moeten bewaren) - dit zet enkel een aanvraag-vlag (deletionRequestedAt)
  // + isActive=false op het account. Een admin krijgt daarvan een notificatie
  // en kiest bij Gebruikers zelf om het account écht te verwijderen of de
  // aanvraag af te wijzen (heractiveren). Vereist door zowel Google Play als
  // Apple: een duidelijk zichtbaar, bereikbaar pad in de app om verwijdering
  // van je account/gegevens aan te vragen (zie ook privacy.section4Text).
  // Geen melding nodig na succes: isActive=false logt de gebruiker meteen uit
  // (onLogout hieronder), dus die ziet toch niets meer van deze pagina.
  const handleConfirmAccountDeletion = async () => {
    try {
      setDeletionSending(true);
      const token = getAuthToken();
      await axios.post(`${API_URL}/api/request-account-deletion`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfirmingDeletion(false);
      onLogout();
    } catch (error) {
      showStatus(error.response?.data?.error || t('profileMenu.deleteAccountError'), 'error');
      setDeletionSending(false);
    }
  };

  return (
    <div className="profile-menu-container" ref={containerRef}>
      {!screen && (
      <button
        className="profile-menu-btn"
        onClick={() => {
          // Dropdown loopt edge-to-edge direct onder de navbar (zelfde look als
          // het hamburgermenu) - de hoogte van de navbar meten we hier.
          const nav = containerRef.current?.closest('nav');
          if (nav) setMenuTop(Math.round(nav.getBoundingClientRect().bottom));
          setOpen(!open);
        }}
        aria-label={t('profileMenu.title')}
      >
        {photoUrl ? (
          <Avatar url={photoUrl} name={user?.name} size={34} />
        ) : (
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="8" r="4" />
            <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
          </svg>
        )}
      </button>
      )}

      {(open || screen) && (
        <div className={screen ? 'profile-menu-dropdown profile-menu-screen' : 'profile-menu-dropdown'} style={screen ? undefined : { top: menuTop }}>
          <div className="profile-menu-identity">
            {user?.role !== 'ADMIN' && <Avatar url={photoUrl} name={user?.name} size={36} />}
            {user?.name && <div className="profile-menu-name profile-menu-name-inline">{user.name}</div>}
          </div>
          <div className="profile-menu-lang-row">
            <span>{t('profileMenu.language')}</span>
            {/* Zelfde taalmenu als op de website (code, naam, vinkje). */}
            <LanguageSwitcher />
          </div>
          <div className="profile-menu-divider" />
          <button onClick={() => { setShowAccountSettings(true); setOpen(false); }}>
            {t('profileMenu.accountSettings')}
          </button>
          {onOpenProperties && (
            <button onClick={() => { setOpen(false); onOpenProperties(); }}>
              {t('profileMenu.properties')}
            </button>
          )}
          <button onClick={() => { setOpen(false); handleContact(); }}>
            {t('profileMenu.contact')}
          </button>
          <button onClick={() => { setOpen(false); openLegal('/privacy'); }}>
            {t('profileMenu.privacyPolicy')}
          </button>
          <button onClick={() => { setOpen(false); openLegal(termsPath); }}>
            {t('profileMenu.termsOfService')}
          </button>
          <div className="profile-menu-divider" />
          {/* Tijdens "Inloggen als" (impersonatie) mag er bewust GEEN
              "Uitloggen"-knop staan die zou kunnen doen vermoeden dat je de
              echte, onderliggende admin-sessie verlaat - die blijft sowieso
              altijd actief (zie utils/authSession.js). In de plaats staat
              hier een expliciete "Terug naar admin"-knop, die technisch
              hetzelfde doet als onLogout hierboven altijd al deed zodra
              isImpersonating() true is (enkel de impersonatie beëindigen en
              terugkeren) - enkel het label/de stijl maakt nu duidelijk dat
              dit geen "echte" uitlog-actie is. */}
          <button className={isImpersonating() ? 'profile-menu-back-to-admin-btn' : 'profile-menu-logout-btn'} onClick={onLogout}>
            {isImpersonating() ? t('profileMenu.backToAdmin') : t('profileMenu.logout')}
          </button>
        </div>
      )}

      {showAccountSettings && (
        <div className="profile-menu-modal-overlay" onClick={() => setShowAccountSettings(false)}>
          <div className="profile-menu-modal" onClick={(e) => e.stopPropagation()}>
            <div className="profile-menu-modal-header">
              <button type="button" className="native-detail-back legal-modal-back" onClick={() => setShowAccountSettings(false)} aria-label={t('common.back')}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 6l-6 6 6 6" /></svg>
              </button>
              <h3>{t('profileMenu.accountSettings')}</h3>
              <button type="button" className="profile-menu-modal-close" onClick={() => setShowAccountSettings(false)} aria-label={t('common.close')}>✖</button>
            </div>
            <div className="profile-menu-settings-body">
              {user?.role !== 'ADMIN' && (
              <div className="profile-menu-settings-photo">
                <Avatar url={photoUrl} name={user?.name} size={88} />
                <div className="profile-menu-settings-photo-actions">
                  <button type="button" className="profile-menu-settings-btn" disabled={photoBusy} onClick={() => photoInputRef.current?.click()}>
                    {photoBusy ? t('common.loading') : (photoUrl ? t('profileMenu.changePhoto') : t('profileMenu.addPhoto'))}
                  </button>
                  {photoUrl && !photoBusy && (
                    <button type="button" className="profile-menu-settings-btn profile-menu-settings-btn-remove" onClick={handleRemovePhoto}>
                      {t('profileMenu.removePhoto')}
                    </button>
                  )}
                </div>
                <input ref={photoInputRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={handlePhotoSelected} />
              </div>
              )}

              <button type="button" className="profile-menu-settings-row" onClick={() => setConfirmingPassword(true)} disabled={sending}>
                {sending ? t('common.loading') : t('profileMenu.changePassword')}
              </button>
              {onOpenMyCode && (
                <button type="button" className="profile-menu-settings-row" onClick={() => { setShowAccountSettings(false); onOpenMyCode(); }}>
                  {t('cleaner.tabMyCode')}
                </button>
              )}
              <button type="button" className="profile-menu-settings-row" onClick={() => { setShowAccountSettings(false); setShowNotifPrefs(true); }}>
                {t('profileMenu.notificationSettings')}
              </button>

              {/* Adminaccounts mogen zichzelf niet verwijderen/deactiveren
                  (enkel een ANDERE admin via Gebruikers; backend blokkeert dit
                  ook) - knop daarom niet tonen voor 'ADMIN'. */}
              {user?.role !== 'ADMIN' && (
                <button
                  type="button"
                  className="profile-menu-settings-row profile-menu-settings-delete"
                  onClick={() => { setShowAccountSettings(false); setConfirmingDeletion(true); }}
                >
                  {t('profileMenu.deleteAccount')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showNotifPrefs && <NotificationPreferences onClose={() => setShowNotifPrefs(false)} />}
      {showContact && <ContactMessage onClose={() => setShowContact(false)} initialMessage={contactPrefill} />}

      {/* Eigen bevestigingspopup i.p.v. window.confirm() - bewust kaal
          gehouden (geen titel/kruisje/uitleg, enkel de vraag + 2 knoppen), op
          expliciet verzoek: dit hoeft geen uitleg te geven, want na
          bevestigen logt de gebruiker toch meteen uit. Zelfstandig gestyled
          (profile-menu-modal-*) zodat dit niet afhangt van welke pagina-CSS
          toevallig al geladen is (ProfileMenu zit in de navbar van meerdere
          pagina's). */}
      {confirmingDeletion && (
        <div
          className="profile-menu-modal-overlay"
          onClick={() => !deletionSending && setConfirmingDeletion(false)}
        >
          <div className="profile-menu-modal profile-menu-modal-compact" onClick={(e) => e.stopPropagation()}>
            <div className="profile-menu-modal-body">
              <p>{t('profileMenu.deleteAccountConfirmText')}</p>
            </div>
            <div className="profile-menu-modal-buttons">
              <button
                type="button"
                className="profile-menu-modal-cancel"
                onClick={() => setConfirmingDeletion(false)}
                disabled={deletionSending}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="profile-menu-modal-confirm"
                onClick={handleConfirmAccountDeletion}
                disabled={deletionSending}
              >
                {deletionSending ? t('common.loading') : t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmingPassword && (
        <div className="profile-menu-modal-overlay" onClick={() => setConfirmingPassword(false)}>
          <div className="profile-menu-modal profile-menu-modal-compact" onClick={(e) => e.stopPropagation()}>
            <div className="profile-menu-modal-body">
              <p>{t('profileMenu.changePasswordConfirmText')}</p>
            </div>
            <div className="profile-menu-modal-buttons">
              <button type="button" className="profile-menu-modal-cancel" onClick={() => setConfirmingPassword(false)}>
                {t('common.cancel')}
              </button>
              <button type="button" className="profile-menu-modal-confirm" onClick={handleChangePassword}>
                {t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Losse, zelf-verdwijnende melding (toast) - enkel nog gebruikt voor
          wachtwoord-reset-status en een eventuele foutmelding bij
          accountverwijdering (bij succes logt de gebruiker meteen uit, geen
          melding nodig). */}
      {status && (
        <div className={`profile-menu-toast ${status.type}`}>{status.text}</div>
      )}
    </div>
  );
}

export default ProfileMenu;
